// Per-line item entity tagging at Import Control (associated-entities.md → AE4).
//
// A SECTION OF THE ADDITIONAL DETAIL POP-UP (owner, 2026-10-28 — D-425: "fold
// the Associated Entities information into the Additional Detail pop-up"):
// `AdditionalDetailPopover.tsx` draws it under the Marketing Category and Fleet
// Maintenance's detail. Every value it shows rather than edits — an entity's
// type, a read-only entity — is the locked box every other field uses
// (`LockedValue`), not text on a gray panel.
// Tags a line item with the individual entities it spends on — Vehicle /
// Staff / Participant — for later per-entity cost rollups (AE5). Self-contained:
// each action is its own write (add / edit / remove) rather than riding the
// invoice form's save, so a mis-tag never blocks the invoice.
//
// Vehicle and Staff resolve through a picker into TYPED reference columns
// (api-contract §9): a Vehicle pick writes `vehicle_id`, the uuid of the
// `vehicles` row, and a Staff pick writes `staff_upn`. There is no single
// overloaded tag column — the schema's CHECK permits only the column matching
// `entity_type`, and the API ignores any other key, which is why the app's old
// `entity_id` made every hand-made tag a silent no-op (M9). `entity_name` carries
// the display value beside the reference (the fleet number, the person's name),
// which is what a resolved-by-worker row stores too, so a manual pick and a
// matched one read identically.
//
// The API stamps `match_source = 'Manual'` on every create and every edit here —
// a human touched the row. The pill still reads "Unmatched" when the row holds no
// reference, because that is the state a reviewer scanning for unresolved tags
// cares about (QA Q26); it is display logic over the data, not a second value.
//
// `entity_type` is FIXED at creation: §9.3 has no `entity_type` in its patch body,
// so on a saved row the type is shown as text rather than a dropdown that would
// look editable and change nothing.
//
// A NEW TAG IS A VEHICLE (owner, 2026-09-29, `docs/decisions.md` D-212): *"The
// solution is not yet ready for Staff and Participant associated entities -
// only Vehicles"*. The add form offers no type choice; it tags a vehicle. A
// Staff or Participant row already on a record still lists, edits (its name or
// staff pick) and removes exactly as before, so old data is not stranded.
//
// PORT NOTE — Participant is as-printed text only. The participant census was a
// direct read of a clinical SQL view; it is not part of this system, so there is
// no picker, no center scope and no resolution for a Participant tag. The
// document text is captured and that is the whole record.
//
// entity_source_text is the document text that triggered the reference,
// verbatim — an as-printed value, so like every other as-printed value in the
// schema it is never overwritten. On an existing row it is read-only: overtyping
// it would destroy the capture record and the key Staff/Vehicle resolution
// matches on.
//
// IT IS NO LONGER SHOWN OR ASKED FOR HERE (owner item 12, 2026-08-28: *"In the
// tagged entities box, get rid of the middle 'Source text' input. We just need
// the entity type and the entity itself."*). Two data columns, not three: the
// type, then the entity.
//
// THE COLUMN IS STILL WRITTEN AND STILL READ — it is not dead, and nothing came
// off the wire. Extraction populates it (`backend/src/lambda/extract.ts`),
// pass-2 vehicle resolution matches on it (`matchingLadder.ts`'s
// `resolveVehicleEntity`), the per-entity analytics rollup coalesces on it
// (`repo/pg/analytics.ts`), and the read-only listing here still falls back
// to it for a Participant when nothing else names one. What changed is only
// this editor: the add form no longer COLLECTS it, so a hand-added row simply has none — which is truthful, since a
// hand-added row never had document text to capture in the first place.
//
// WHAT THAT COST, AND HOW IT IS PAID: source text was the ONLY content a
// Participant row could carry, here and on the wire (there is no participant
// lookup — see the PORT NOTE above). So the entity column now holds a free-text
// NAME for a Participant instead of the picker the other two types get, written
// to `entity_name`; and an EXISTING Participant row displays
// `entity_name || entity_source_text`, so an AI-captured participant still reads
// out under the new layout rather than vanishing with the column.
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Caption1,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { AddRegular, DeleteRegular } from '@fluentui/react-icons'
import type { AssociatedEntity, InvoiceLineItem, Invoice } from '../lib/types'
import {
  createAssociatedEntity,
  deactivateAssociatedEntity,
  loadAssociatedEntities,
  searchStaff,
  searchVehicles,
  updateAssociatedEntity,
} from '../lib/data'
import { errorMessage } from '../api/errors'
import { sharedGroupTags } from '@ap/shared'
import { combinedEntities } from '../lib/lineItemBulk'
import { DotPill } from './Badges'
import { EntityPicker, type EntityOption } from './EntityPicker'
import { LockedValue } from './LockedValue'
import { isUnmatchedTag } from '../lib/required'
import { TINT_PILLS, useLineItemPanelStyles, useRequiredStyles } from '../lib/theme'

/** The section's heading inside the Additional Detail pop-up (D-425). */
const ASSOCIATED_ENTITIES_LABEL = 'Associated Entities'

/** Every type a STORED row may carry — what the list must still display. */
const ENTITY_TYPES = ['Vehicle', 'Staff', 'Participant'] as const
type EntityType = (typeof ENTITY_TYPES)[number]

/** The one type a person may ADD (D-212). */
const ADD_TYPE: EntityType = 'Vehicle'

/** Participant has no picker — nothing resolves a participant here. */
function hasPicker(type: EntityType): boolean {
  return type !== 'Participant'
}

// The picker's search, chosen by the row's entity type (searchVehicles /
// searchStaff already return the EntityPicker option shape).
function searchForType(type: EntityType): (term: string) => Promise<EntityOption[]> {
  if (type === 'Staff') return (t) => searchStaff(t)
  return (t) => searchVehicles(t)
}

function pickerPlaceholder(type: EntityType): string {
  return type === 'Staff' ? 'Search staff…' : 'Search vehicles…'
}

// The heading and the quiet note (`head`, `headLabel`, `note`) are the
// Additional Detail pop-up's — `useLineItemPanelStyles` in `../lib/theme.ts`.
const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: '8px' },
  list: { display: 'flex', flexDirection: 'column', gap: '6px' },
  // A tagged-entity row: type | entity | match-source pill | actions (owner
  // item 12, 2026-08-28 — the source-text column that sat between the first two
  // is gone). The entity column takes the width the two used to share.
  row: {
    display: 'grid',
    gridTemplateColumns: '128px minmax(0, 1fr) auto auto',
    alignItems: 'center',
    gap: '6px',
  },
  rowActions: { display: 'flex', alignItems: 'center', gap: '2px' },
  error: { color: tokens.colorPaletteRedForeground1 },
})

const matchSourcePill = (source: string | undefined) =>
  !source || source === 'Unmatched' ? TINT_PILLS.yellow : TINT_PILLS.green

function typeOf(v: string | undefined): EntityType {
  return (ENTITY_TYPES as readonly string[]).includes(v ?? '') ? (v as EntityType) : 'Vehicle'
}

/** The reference this row resolved to, read out of the column its type uses.
    A Participant row never has one — that is the whole design, not a gap. */
function referenceIdOf(ae: AssociatedEntity): string {
  const type = typeOf(ae.entity_type)
  if (type === 'Vehicle') return ae.vehicle_id ?? ''
  if (type === 'Staff') return ae.staff_upn ?? ''
  return ''
}

/** The typed reference column for `type`, as a write body. `null` clears the
    pick; the column for the OTHER type is sent as null on an edit so a row can
    never hold two references (the schema CHECK refuses it outright). */
function referenceFields(
  type: EntityType,
  entityId: string | undefined,
  mode: 'create' | 'edit',
): { vehicle_id?: string | null; staff_upn?: string | null } {
  const id = entityId || null
  if (type === 'Vehicle') return mode === 'create' && id === null ? {} : { vehicle_id: id }
  if (type === 'Staff') return mode === 'create' && id === null ? {} : { staff_upn: id }
  return {} // Participant: as-printed text only, no reference column
}

/** Hidden for now (owner, 2026-11-02 — D-448): nothing else tells AI-recorded from human-recorded values. */
const SHOW_MATCH_SOURCE_PILL = false

/** The pill's words for a stored `match_source`; the stored value is unchanged (D-448). */
const MATCH_SOURCE_LABELS: Readonly<Record<string, string>> = { VehicleList: 'Matched' }

/** What the pill should say. The API records that a human set the row; a row with
    no reference is still unresolved, whatever set it (QA Q26). */
function displayedMatchSource(ae: AssociatedEntity): string {
  const source =
    typeOf(ae.entity_type) === 'Participant'
      ? (ae.match_source ?? 'Unmatched')
      : referenceIdOf(ae)
        ? (ae.match_source ?? 'Manual')
        : 'Unmatched'
  return MATCH_SOURCE_LABELS[source] ?? source
}

interface Props {
  invoice: Invoice
  lineItem: InvoiceLineItem
  /** Fired after a tag is added, edited or removed — never on the initial
      load, which would make a parent that re-renders on it loop. The line-item
      table uses it to re-read which lines still carry an UNMATCHED tag, so the
      red ring on the link button clears the moment the tag is resolved
      (owner, 2026-09-14). */
  onChanged?: () => void
  /** List the line's entities with no add, save or remove — the read-only
      steps (D-154). Each value is a locked box (D-425). */
  readOnly?: boolean
  /** List the tags of every one of these lines in place of `lineItem`'s, one
      per entity. Pass it with `readOnly`. */
  childIds?: readonly string[]
  /** How `childIds` are listed: `union`, every tag any of them carries — the
      line-item bulk row's proposed line (D-242); `shared`, the tags every one
      of them carries — a group row, whose lines must agree (D-425), and none
      with a red sentence when they do not. */
  childMode?: 'union' | 'shared'
}

/** What a group row's Associated Entities say when its lines' tags differ (D-425). */
const GROUP_TAGS_DIFFER = 'The lines in this group do not all have the same entities.'

export function AssociatedEntitiesEditor({ invoice, lineItem, onChanged, readOnly, childIds, childMode = 'union' }: Props) {
  const s = useStyles()
  const ps = useLineItemPanelStyles()
  const headerId = invoice.id ?? ''
  const childId = lineItem.id
  const childIdsKey = childIds === undefined ? undefined : childIds.join('|')
  const [rows, setRows] = useState<AssociatedEntity[] | 'loading'>('loading')
  // A group row's lines carry different tags (`childMode` 'shared').
  const [differ, setDiffer] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Inline add form. It tags a Vehicle and nothing else (D-212).
  const [adding, setAdding] = useState(false)
  // The vehicle being tagged: `id` + display name from the picker.
  const [addEntity, setAddEntity] = useState<{ id?: string; name?: string }>({})
  // Monotonic load id so a slow refresh can't overwrite a newer one.
  const loadSeq = useRef(0)
  // THE HOST FOR EVERY POPUP IN HERE IS THIS EDITOR'S OWN ROOT, which is inside
  // the Popover. It used to be the invoice Dialog's surface, reached by
  // `.closest('[role="dialog"]')`, and that made the vehicle and staff pickers
  // unusable: a listbox mounted at the dialog is OUTSIDE the Popover, so
  // clicking one of its options was an outside-click, and Fluent's Popover
  // dismissed the whole editor before the pick could be committed. Every
  // hand-added row ended up `Unmatched` with an empty picker, and there was no
  // way to attach a vehicle at all.
  //
  // Mounting inside the editor keeps the stacking fix the dialog host was
  // chosen for (`popupPositionProps` + `usePopupListboxClass` raise the listbox
  // over the popover's own `zIndex: 10` surface) while putting the options
  // inside the surface that is listening for outside clicks.
  const rootRef = useRef<HTMLDivElement>(null)
  const [popupHost, setPopupHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setPopupHost(rootRef.current)
  }, [])

  const refresh = useCallback(async () => {
    const seq = ++loadSeq.current
    if (!headerId) {
      setRows([])
      return
    }
    try {
      const all = await loadAssociatedEntities(headerId)
      if (seq !== loadSeq.current) return
      if (childIdsKey !== undefined && childMode === 'shared') {
        const perLine = childIdsKey.split('|').map((id) => all.filter((e) => e.child_id === id && e.is_active !== false))
        const shared = sharedGroupTags(perLine)
        setDiffer(shared.length === 0 && perLine.some((tags) => tags.length > 0))
        setRows(shared)
        return
      }
      setDiffer(false)
      setRows(
        childIdsKey !== undefined
          ? combinedEntities(all, new Set(childIdsKey.split('|')))
          : all.filter((e) => e.child_id === childId),
      )
    } catch (e) {
      if (seq !== loadSeq.current) return
      setRows([])
      setError(`Could not load entities: ${errorMessage(e)}`)
    }
  }, [headerId, childId, childIdsKey, childMode])

  useEffect(() => {
    refresh()
  }, [refresh])

  // PORT NOTE: each action used to also write its own correction row for the
  // feedback loop. The API writes corrections itself now, from the change it
  // applied, so there is nothing to mirror here.

  async function handleAdd() {
    const type = ADD_TYPE
    const entityId = addEntity.id
    const entityName = (addEntity.name ?? '').trim()
    if (!entityId && !entityName) return // nothing meaningful to tag
    setBusy(true)
    setError(null)
    try {
      // NO `entity_source_text` (owner item 12, 2026-08-28 — this file's header
      // comment carries the whole rule). The API still accepts it on a create
      // and the column is still written by extraction; a HAND-ADDED row simply
      // has no document text to capture, so it sends none.
      await createAssociatedEntity(headerId, {
        child_id: childId,
        entity_type: type,
        entity_name: entityName || undefined,
        // The typed reference column for this type, and only that one.
        // `match_source` is not sent: §9.2 stamps 'Manual' on anything created
        // here, because a hand-added row is by definition a human's.
        ...referenceFields(type, entityId, 'create'),
      })
      setAdding(false)
      setAddEntity({})
      await refresh()
      onChanged?.()
    } catch (e) {
      setError(`Could not add entity: ${errorMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleSaveRow(
    ae: AssociatedEntity,
    draft: { entity: { id?: string; name?: string } },
  ) {
    // The row's own type: §9.3 cannot change it, so the edit is the reference and
    // its display name. `entity_source_text` is not sent AT ALL — its mere
    // presence in a patch body is a 400, because it is as-printed provenance.
    const type = typeOf(ae.entity_type)
    const entityId = draft.entity.id ?? ''
    const entityName = draft.entity.name ?? ''
    setBusy(true)
    setError(null)
    try {
      await updateAssociatedEntity(ae.id, {
        // Empty clears the pick; a populated id resolves the row.
        ...referenceFields(type, entityId, 'edit'),
        entity_name: entityName,
        row_version: ae.row_version,
      })
      await refresh()
      onChanged?.()
    } catch (e) {
      setError(`Could not save entity: ${errorMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleRemove(ae: AssociatedEntity) {
    setBusy(true)
    setError(null)
    try {
      await deactivateAssociatedEntity(ae.id, ae.row_version)
      await refresh()
      onChanged?.()
    } catch (e) {
      setError(`Could not remove entity: ${errorMessage(e)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={s.section} ref={rootRef}>
      <div className={ps.head}>
        <span className={ps.headLabel}>{ASSOCIATED_ENTITIES_LABEL}</span>
        {rows === 'loading' && <Spinner size="tiny" />}
      </div>

      {error && <Caption1 className={s.error}>{error}</Caption1>}

      {rows !== 'loading' && rows.length === 0 && !adding && (
        differ ? (
          <Caption1 className={s.error}>{GROUP_TAGS_DIFFER}</Caption1>
        ) : (
          <Caption1 className={ps.note}>{childIds === undefined ? 'No associated entities are tagged for this line item.' : 'No associated entities are tagged for these line items.'}</Caption1>
        )
      )}

      {rows !== 'loading' && rows.length > 0 && (
        <div className={s.list}>
          {rows.map((ae) => (
            <EntityRow
              key={ae.id}
              ae={ae}
              readOnly={readOnly}
              busy={busy}
              onSave={(draft) => handleSaveRow(ae, draft)}
              onRemove={() => handleRemove(ae)}
              popupHost={popupHost}
            />
          ))}
        </div>
      )}

      {readOnly ? null : adding ? (
        <div className={s.row}>
          {/* The type is fixed text, not a dropdown: a new tag is a Vehicle
              (D-212). Staff and Participant are no longer offered. */}
          <LockedValue boxed value={ADD_TYPE} ariaLabel="Entity type" />
          <EntityPicker
            size="small"
            mountNode={popupHost}
            ariaLabel={`${ADD_TYPE} to tag`}
            value={addEntity}
            placeholder={pickerPlaceholder(ADD_TYPE)}
            search={searchForType(ADD_TYPE)}
            onSelect={(o) => setAddEntity({ id: o.id, name: o.primary })}
          />
          <Button size="small" appearance="primary" disabled={busy || !addEntity.id && !(addEntity.name ?? '').trim()} onClick={handleAdd}>
            Add
          </Button>
          <Button size="small" appearance="subtle" disabled={busy} onClick={() => { setAdding(false); setAddEntity({}) }}>
            Cancel
          </Button>
        </div>
      ) : (
        // ONE ASSOCIATED ENTITY PER LINE (owner, 2026-10-28 — D-428): Add
        // Entity is offered only while the line has none. The one it has can
        // still be edited or removed; the server refuses a second
        // (`line_entity_limit`).
        rows !== 'loading' && !rows.some((ae) => ae.is_active !== false) && (
          <div>
            <Button size="small" appearance="subtle" icon={<AddRegular />} disabled={busy} onClick={() => setAdding(true)}>
              Add Entity
            </Button>
          </div>
        )
      )}
    </div>
  )
}

// A single existing tagged-entity row with its own draft state (seeded once from
// the loaded row). The Save button lights up only when the draft differs.
function EntityRow({
  ae,
  readOnly,
  busy,
  onSave,
  onRemove,
  popupHost,
}: {
  ae: AssociatedEntity
  /** A locked box in place of the picker, and no Save or Remove. */
  readOnly?: boolean
  busy: boolean
  onSave: (draft: { entity: { id?: string; name?: string } }) => void
  onRemove: () => void
  /** Where this row's picker mounts its listbox — the editor's own root, so an
      option click lands INSIDE the Popover. See the resolution above. */
  popupHost: HTMLElement | null
}) {
  const s = useStyles()
  const rs = useRequiredStyles()
  // AN UNMATCHED TAG IS YELLOW (owner, 2026-11-02 — D-452): its flag is
  // unresolved and holds the release until the tag is corrected or removed. A
  // tag is never required, so never red.
  const flaggedClass = isUnmatchedTag(ae) ? rs.flagged : undefined
  // FIXED, not a draft: §9.3 carries no `entity_type`, so the type a row was
  // created with is the type it keeps.
  const type = typeOf(ae.entity_type)
  // A Participant carries no reference to resolve, so what it shows is its own
  // name — falling back to the as-printed source text, which is what an
  // AI-captured participant actually holds and was all the removed source-text
  // column ever displayed for one (owner item 12, 2026-08-28). Read straight
  // off the row, never held as draft state: nothing here edits either value.
  const participantLabel = (ae.entity_name ?? '').trim() || (ae.entity_source_text ?? '').trim()
  const [entity, setEntity] = useState<{ id?: string; name?: string }>(() => ({
    id: referenceIdOf(ae) || undefined,
    name: ae.entity_name || undefined,
  }))

  // Adopt server-side changes (e.g. the directory worker resolving an entity
  // after load) into the draft — but ONLY while the draft still matched the
  // PREVIOUS server row, so an in-progress edit is never clobbered.
  const prevAeRef = useRef(ae)
  useEffect(() => {
    const prev = prevAeRef.current
    prevAeRef.current = ae
    const serverChanged =
      referenceIdOf(ae) !== referenceIdOf(prev) ||
      (ae.entity_name ?? '') !== (prev.entity_name ?? '')
    if (!serverChanged) return
    const wasUntouched =
      (entity.id ?? '') === referenceIdOf(prev) &&
      (entity.name ?? '') === (prev.entity_name ?? '')
    if (!wasUntouched) return
    setEntity({ id: referenceIdOf(ae) || undefined, name: ae.entity_name || undefined })
  }, [ae, entity])

  const dirty =
    (entity.id ?? '') !== referenceIdOf(ae) ||
    (entity.name ?? '') !== (ae.entity_name ?? '')

  return (
    <div className={s.row}>
      <LockedValue boxed value={type} ariaLabel="Entity type" />
      {/* NO SOURCE-TEXT CELL (owner item 12, 2026-08-28) — see this file's
          header comment. A Participant, which has no picker, shows its own
          name here instead, falling back to the captured source text so an
          AI-tagged participant still reads out. */}
      {readOnly ? (
        <LockedValue boxed value={(ae.entity_name ?? '').trim() || participantLabel || 'No name recorded'} ariaLabel="Entity" className={flaggedClass} />
      ) : hasPicker(type) ? (
        <EntityPicker
          key={type}
          size="small"
          mountNode={popupHost}
          ariaLabel={`${type} to tag`}
          value={entity}
          placeholder={pickerPlaceholder(type)}
          search={searchForType(type)}
          onSelect={(o) => setEntity({ id: o.id, name: o.primary })}
          className={flaggedClass}
        />
      ) : (
        <LockedValue boxed value={participantLabel || 'No name recorded'} ariaLabel="Entity" />
      )}
      {SHOW_MATCH_SOURCE_PILL && (
        <DotPill pill={matchSourcePill(displayedMatchSource(ae))} title={`Match source: ${displayedMatchSource(ae)}`}>
          {displayedMatchSource(ae)}
        </DotPill>
      )}
      <div className={s.rowActions}>
        {!readOnly && dirty && (
          <Button size="small" appearance="primary" disabled={busy} onClick={() => onSave({ entity })}>
            Save
          </Button>
        )}
        {!readOnly && (
          <Button size="small" appearance="subtle" icon={<DeleteRegular />} aria-label="Remove Entity" disabled={busy} onClick={onRemove} />
        )}
      </div>
    </div>
  )
}
